import { formatUserFacingError } from '@/lib/errors'
import { useState, useRef } from 'react'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  storage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from '@/lib/firebase/storage'
import { testimonialService } from '../services/testimonial.service'

interface AdminPhotoUploadDialogProps {
  testimonialId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (url: string) => void
}

export function AdminPhotoUploadDialog({
  testimonialId,
  open,
  onOpenChange,
  onSuccess,
}: AdminPhotoUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
    setError(null)

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(file ? URL.createObjectURL(file) : null)
  }

  function handleClose(open: boolean) {
    if (!uploading) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      setSelectedFile(null)
      setPreviewUrl(null)
      setProgress(0)
      setError(null)
      onOpenChange(open)
    }
  }

  async function handleUpload() {
    if (!selectedFile) return
    setUploading(true)
    setError(null)

    const storageRef = ref(storage, `testimonial-photos/${testimonialId}`)
    const task = uploadBytesResumable(storageRef, selectedFile)

    await new Promise<void>((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          setProgress(pct)
        },
        (err) => reject(err),
        () => resolve()
      )
    })
      .then(async () => {
        const downloadUrl = await getDownloadURL(storageRef)
        await testimonialService.updatePhotoUrl(testimonialId, downloadUrl)
        onSuccess(downloadUrl)
        handleClose(false)
      })
      .catch((err: unknown) => {
        setError(formatUserFacingError(err, 'Upload failed. Please try again.'))
      })
      .finally(() => {
        setUploading(false)
        setProgress(0)
      })
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Upload Client Photo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview */}
          {previewUrl ? (
            <div className="flex justify-center">
              <img
                src={previewUrl}
                alt="Preview"
                className="h-32 w-32 rounded-full object-cover ring-2 ring-muted"
              />
            </div>
          ) : (
            <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 text-muted-foreground">
              <div className="text-center">
                <Upload className="mx-auto mb-1 h-6 w-6" />
                <p className="text-xs">No image selected</p>
              </div>
            </div>
          )}

          {/* File input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Choose Image
          </Button>

          {/* Progress bar */}
          {uploading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Uploading…</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-brand-orange transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={() => void handleUpload()} disabled={!selectedFile || uploading}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
